// The one language picker, used by the page bar and all three editors

import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { Globe, Settings2 } from "lucide-react"
import { describeLanguage, type LanguageInfo, type TranslationStatus, STATUS_LABEL } from "../../languages"
import { StatusDot } from "./StatusDot"

const MANAGE = "__manage_languages__"

export interface LanguageSwitcherProps {
  languages: LanguageInfo[]
  value: string
  onChange: (code: string) => void
  sourceLocale: string
  // This record's state in each language; omitted for the page-level switcher
  statusOf?: (code: string) => TranslationStatus
  // Short text on the right of each option, e.g. coverage or publish state
  detailOf?: (code: string) => string | undefined
  onManage?: () => void
  className?: string
}

export function LanguageSwitcher({
  languages, value, onChange, sourceLocale, statusOf, detailOf, onManage, className,
}: LanguageSwitcherProps) {
  // The working language always shows, even if this section doesn't list it yet
  const options = languages.some(l => l.code === value) ? languages : [...languages, describeLanguage(value)]
  const current = options.find(l => l.code === value)!
  const currentStatus = statusOf?.(value)

  return (
    <Select value={value} onValueChange={v => (v === MANAGE ? onManage?.() : onChange(v))}>
      <SelectTrigger
        className={cn("h-8 w-auto min-w-[150px] max-w-[240px] gap-1.5 px-2.5 text-xs", className)}
        title={`Working in ${current.name} (${current.code})${currentStatus ? ` · ${STATUS_LABEL[currentStatus]}` : ""}`}
      >
        {/* A div: the trigger line-clamps its direct span children, which breaks a flex row */}
        <div className="flex min-w-0 items-center gap-1.5">
          {currentStatus
            ? <StatusDot status={currentStatus} />
            : <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          <span className="truncate font-medium">{current.name}</span>
          <span className="shrink-0 font-mono text-[10px] leading-none text-muted-foreground">{current.code}</span>
        </div>
      </SelectTrigger>
      <SelectContent align="end" className="min-w-[260px]">
        {options.map(l => {
          const status = statusOf?.(l.code) ?? (l.code === sourceLocale ? "source" : undefined)
          const detail = detailOf?.(l.code)
          return (
            <SelectItem key={l.code} value={l.code} className="text-xs">
              <span className="flex w-full min-w-0 items-center gap-2">
                {status ? <StatusDot status={status} /> : <span className="w-2 shrink-0" />}
                <span className="truncate">{l.name}</span>
                <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{l.code}</span>
                {l.direction === "rtl" && <span className="shrink-0 text-[9px] uppercase text-muted-foreground">rtl</span>}
                <span className="ml-auto shrink-0 pl-3 text-[10px] text-muted-foreground">
                  {detail ?? (status && status !== "source" ? STATUS_LABEL[status] : status === "source" ? "Source" : "")}
                </span>
              </span>
            </SelectItem>
          )
        })}
        {onManage && (
          <>
            <SelectSeparator />
            <SelectItem value={MANAGE} className="text-xs text-muted-foreground">
              <span className="flex items-center gap-2">
                <Settings2 className="h-3.5 w-3.5" /> Manage languages…
              </span>
            </SelectItem>
          </>
        )}
      </SelectContent>
    </Select>
  )
}
